/**
 * Pure browser detection helper used by NotificationHelp.vue to tailor
 * per-browser instructions.
 *
 * Detection order matters:
 *  - Edge (Chromium) UA contains both "Edg/" AND "Chrome/" — test Edge FIRST
 *  - Chrome UA contains "Chrome/" but NOT "Edg/" or "OPR/"
 *  - Firefox UA contains "Firefox/"
 *  - Safari UA contains "Safari/" but NOT "Chrome/" or "CriOS/" (Chrome-on-iOS)
 *  - Anything else returns 'other'
 *
 * We deliberately don't detect Opera, Brave, Vivaldi, etc. — users of those
 * get 'other' and see a generic fallback; they're <1% of Teamio traffic.
 *
 * Pure function: takes the UA string explicitly so it's testable in Node.
 *
 * Unit tests: cypress/node-tests/browser-detection.test.js
 */

export const BROWSERS = {
  chrome:  { key: 'chrome',  label: 'Chrome',  icon: 'fa-chrome' },
  edge:    { key: 'edge',    label: 'Edge',    icon: 'fa-edge' },
  firefox: { key: 'firefox', label: 'Firefox', icon: 'fa-firefox' },
  safari:  { key: 'safari',  label: 'Safari',  icon: 'fa-safari' },
  other:   { key: 'other',   label: 'Other',   icon: 'fa-globe' },
};

/**
 * @param {string|undefined} userAgent
 * @returns {'chrome'|'edge'|'firefox'|'safari'|'other'}
 */
export function detectBrowser(userAgent) {
  if (!userAgent || typeof userAgent !== 'string') return 'other';
  const ua = userAgent;

  // Edge (Chromium) must be tested before Chrome — its UA has both markers
  if (/Edg\//.test(ua)) return 'edge';

  // Opera, Brave, Vivaldi all have "Chrome/" too; they fall through to 'chrome'
  // intentionally. Safari-on-iOS Chrome has "CriOS/" — classify as chrome.
  if (/Chrome\/|CriOS\//.test(ua)) return 'chrome';

  if (/Firefox\/|FxiOS\//.test(ua)) return 'firefox';

  // Safari must be last — its UA contains "Safari/" but so do Chrome and Edge.
  // By this point we've ruled those out.
  if (/Safari\//.test(ua)) return 'safari';

  return 'other';
}

/**
 * Voice typing needs Chrome's on-device speech engine. Brave strips it (its UA is identical to
 * Chrome's, so the caller passes the `navigator.brave` check), and Firefox / Safari never had it.
 * Returns the message to show, or '' when this browser should be left to feature detection
 * (Chrome, Edge, and anything unrecognised).
 * @param {string|undefined} userAgent
 * @param {boolean} isBrave
 * @returns {string}
 */
export function voiceBrowserBlock(userAgent, isBrave) {
  if (isBrave) return 'Voice typing is only available in Google Chrome. Brave doesn\'t include the speech engine it needs.';
  const key = detectBrowser(userAgent);
  if (key === 'firefox' || key === 'safari') return 'Voice typing is only available in Google Chrome.';
  return '';
}

/**
 * Returns the BROWSERS descriptor for the detected browser, with a safe fallback.
 */
export function getBrowserDescriptor(key) {
  return BROWSERS[key] || BROWSERS.other;
}
